import { BlockMapBuilder, ContentBlock, ContentState, EditorState, SelectionState, genKey } from 'draft-js'
import { List } from 'immutable'

// Inserts that can sit first in a section or back to back, leaving no editable
// line above them (see insertAtomicBlockWithoutExtraLine).
const LINE_ABOVE_INSERT_TYPES = ['TABLE', 'DASHBOARD', 'IFRAME', 'SLICER']

export const INSERT_CLASS = 'line-above-insert'
export const SELECTED_INSERT_CLASS = 'insert-selected'

export const isLineAboveInsert = (content: ContentState, block: ContentBlock | undefined): boolean => {
  if (!block || block.getType() !== 'atomic') return false
  const entityKey = block.getEntityAt(0)
  if (!entityKey) return false
  let type: string
  try {
    type = content.getEntity(entityKey).getType()
  } catch {
    // A dangling entity key (getEntity throws), which the block renderer tolerates too
    return false
  }
  return LINE_ABOVE_INSERT_TYPES.includes(type)
}

const hasNoLineAbove = (content: ContentState, blockKey: string): boolean => {
  const blockBefore = content.getBlockBefore(blockKey)
  return !blockBefore || blockBefore.getType() === 'atomic'
}

const cursorAt = (block: ContentBlock, edge: 'start' | 'end'): SelectionState => {
  const offset = edge === 'start' ? 0 : block.getLength()
  return SelectionState.createEmpty(block.getKey()).merge({
    anchorOffset: offset,
    focusOffset: offset
  }) as SelectionState
}

const emptyLine = () => new ContentBlock({ key: genKey(), type: 'unstyled', text: '', characterList: List() })

// Key of the insert the collapsed cursor is on, or null.
export const getSelectedInsertKey = (editorState: EditorState): string | null => {
  const selection = editorState.getSelection()
  if (!selection.isCollapsed()) return null
  const content = editorState.getCurrentContent()
  const key = selection.getStartKey()
  return isLineAboveInsert(content, content.getBlockForKey(key)) ? key : null
}

export const selectInsert = (editorState: EditorState, blockKey: string): EditorState =>
  EditorState.forceSelection(editorState, SelectionState.createEmpty(blockKey))

export const insertLineAbove = (editorState: EditorState, blockKey: string): EditorState => {
  const content = editorState.getCurrentContent()
  const newBlock = emptyLine()
  const blocks: ContentBlock[] = []
  content.getBlockMap().forEach((block, key) => {
    if (key === blockKey) blocks.push(newBlock)
    blocks.push(block!)
  })
  const cursor = SelectionState.createEmpty(newBlock.getKey())
  const newContent = content.merge({
    blockMap: BlockMapBuilder.createFromArray(blocks),
    selectionBefore: editorState.getSelection(),
    selectionAfter: cursor
  }) as ContentState
  return EditorState.forceSelection(EditorState.push(editorState, newContent, 'insert-fragment'), cursor)
}

// Enter while an insert is selected adds an empty line above it.
export const insertLineAboveSelectedInsert = (editorState: EditorState): EditorState | null => {
  const key = getSelectedInsertKey(editorState)
  return key ? insertLineAbove(editorState, key) : null
}

// The browser's cursor skips over inserts, so arrow keys never land on one. Up arrow instead:
// - from the first line of text directly below an insert, selects that insert;
// - from a selected insert, moves to the text above it, or adds an empty line when there is
//   nothing editable above it (top of the section, or directly below another insert).
export const arrowUpAroundInsert = (editorState: EditorState, cursorOnFirstLine: boolean): EditorState | null => {
  const selection = editorState.getSelection()
  if (!selection.isCollapsed()) return null
  const content = editorState.getCurrentContent()
  const key = selection.getStartKey()
  const blockBefore = content.getBlockBefore(key)

  if (getSelectedInsertKey(editorState)) {
    if (hasNoLineAbove(content, key)) return insertLineAbove(editorState, key)
    return EditorState.forceSelection(editorState, cursorAt(blockBefore!, 'end'))
  }
  if (!cursorOnFirstLine || content.getBlockForKey(key)?.getType() === 'atomic') return null
  return isLineAboveInsert(content, blockBefore) ? selectInsert(editorState, blockBefore!.getKey()) : null
}

// Down arrow from a selected insert moves onto the next block (selecting it when it is an insert too).
export const arrowDownFromInsert = (editorState: EditorState): EditorState | null => {
  const key = getSelectedInsertKey(editorState)
  if (!key) return null
  const content = editorState.getCurrentContent()
  const blockAfter = content.getBlockAfter(key)
  if (!blockAfter) return null
  if (isLineAboveInsert(content, blockAfter)) return selectInsert(editorState, blockAfter.getKey())
  return EditorState.forceSelection(editorState, cursorAt(blockAfter, 'start'))
}

// Removes a block, or swaps it for an empty line when it is the only one (a section always keeps a block).
const removeBlock = (editorState: EditorState, key: string, cursorAfter?: SelectionState): EditorState => {
  const content = editorState.getCurrentContent()
  const line = cursorAfter ? null : emptyLine()
  const cursor = cursorAfter ?? cursorAt(line!, 'start')
  const newContent = content.merge({
    blockMap: line ? BlockMapBuilder.createFromArray([line]) : content.getBlockMap().delete(key),
    selectionBefore: editorState.getSelection(),
    selectionAfter: cursor
  }) as ContentState
  return EditorState.forceSelection(EditorState.push(editorState, newContent, 'remove-range'), cursor)
}

// Backspace/Delete on a selected insert removes the insert. Draft's defaults would instead merge it
// into the line above or delete the insert above it.
const removeSelectedInsert = (editorState: EditorState): EditorState | null => {
  const key = getSelectedInsertKey(editorState)
  if (!key) return null
  const content = editorState.getCurrentContent()
  const blockBefore = content.getBlockBefore(key)
  const blockAfter = content.getBlockAfter(key)
  if (blockBefore) return removeBlock(editorState, key, cursorAt(blockBefore, 'end'))
  return removeBlock(editorState, key, blockAfter && cursorAt(blockAfter, 'start'))
}

// Backspace/Delete in an empty line directly above an insert removes just that line. Draft's
// defaults would instead delete the insert below (Delete) or an insert above it (Backspace).
const removeEmptyLineAboveInsert = (editorState: EditorState): EditorState | null => {
  const selection = editorState.getSelection()
  if (!selection.isCollapsed()) return null
  const content = editorState.getCurrentContent()
  const key = selection.getStartKey()
  const block = content.getBlockForKey(key)
  if (!block || block.getType() !== 'unstyled' || block.getLength() > 0) return null
  const blockAfter = content.getBlockAfter(key)
  if (!isLineAboveInsert(content, blockAfter)) return null

  const blockBefore = content.getBlockBefore(key)
  return removeBlock(editorState, key, blockBefore ? cursorAt(blockBefore, 'end') : cursorAt(blockAfter!, 'start'))
}

export const deleteAroundInsert = (editorState: EditorState, command: string): EditorState | null => {
  if (command !== 'backspace' && command !== 'delete') return null
  return removeSelectedInsert(editorState) ?? removeEmptyLineAboveInsert(editorState)
}

const blockElement = (container: HTMLElement, key: string) =>
  container.querySelector<HTMLElement>(`[data-block="true"][data-offset-key="${key}-0-0"]`)

// What a click in the editor at clientY means for an insert: it landed on the insert itself
// ('select'), or in the gap above an insert with no editable line above it ('line-above').
export const findInsertAtClick = (
  container: HTMLElement,
  clientY: number,
  content: ContentState
): { blockKey: string; action: 'select' | 'line-above' } | null => {
  for (const block of content.getBlocksAsArray()) {
    const blockKey = block.getKey()
    if (!isLineAboveInsert(content, block)) continue
    const element = blockElement(container, blockKey)
    if (!element) continue
    const { top, bottom } = element.getBoundingClientRect()
    if (clientY >= top && clientY < bottom) return { blockKey, action: 'select' }
    if (!hasNoLineAbove(content, blockKey)) continue
    const blockBefore = content.getBlockBefore(blockKey)
    const gapTop = blockBefore
      ? (blockElement(container, blockBefore.getKey())?.getBoundingClientRect().bottom ?? Infinity)
      : -Infinity
    if (clientY >= gapTop && clientY < top) return { blockKey, action: 'line-above' }
  }
  return null
}

// Put the browser's selection on the insert too, so Draft's next native selection read keeps it there.
export const placeDomSelectionOnInsert = (container: HTMLElement, blockKey: string): void => {
  const element = blockElement(container, blockKey)
  const selection = window.getSelection()
  if (!element || !selection) return
  const range = document.createRange()
  range.setStart(element, 0)
  range.collapse(true)
  selection.removeAllRanges()
  selection.addRange(range)
}

// Whether the cursor sits on the first visual line of its block, so Up arrow would leave the block.
export const isCursorOnFirstLine = (): boolean => {
  const selection = window.getSelection()
  if (!selection?.rangeCount) return true
  const range = selection.getRangeAt(0)
  const cursorRect = typeof range.getClientRects === 'function' ? range.getClientRects()[0] : undefined
  const node = range.startContainer
  const block = (node instanceof Element ? node : node.parentElement)?.closest('[data-block="true"]')
  // An empty line has no cursor rect, and is a single line anyway
  if (!cursorRect || !block || cursorRect.height === 0) return true
  return cursorRect.top - block.getBoundingClientRect().top < cursorRect.height
}
